import {
  switchMap,
  map,
  withLatestFrom,
  filter,
  mapTo,
  mergeMap,
  throttleTime,
  pluck,
  catchError,
  take,
  tap,
  concatMap,
  delay
} from 'rxjs/operators'
import { of } from 'rxjs'
import { ofType } from 'redux-observable'
import Actions from '../actions'
import { getPayload, catchAndDispatchError } from '../utils/rxUtils'
import {
  getJSON,
  putJSON,
  putFormData,
  postJSON,
  patchJSON,
  setAuthToken
} from '../utils/fetchUtils'
import mime from 'mime'
import short from 'short-uuid'
import {
  catchAndDispatchCustomError,
  catchAndCancelActions,
  translateErrorMsg
} from '../utils/customErrorHandler'
import { HMOService, backendUrl, clinicTypes, routes } from '../consts'
import { serializeLocationVerification } from '../utils/serializerUtil'
import {
  filterAppointmentsInQueue,
  buildTimeWindowsUrl
} from '../utils/appointmentsUtils'
import { event, eventTypes } from '../consts/eventConsts'
import { formatClientSideError } from '../utils/eventUtils'
import { uriEncodeSearchParams } from '../utils/encodingUtils'
import { appendDirectoryAccessSource } from '../utils/directoryAccessUtils'
import {
  isDmServiceAvailable,
  isDmServiceSelected,
  isHtnServiceAvailable,
  isHtnServiceSelected,
  isMhServiceAvailable,
  isMhServiceSelected,
  isPrimaryServiceAvailable,
  isPrimaryServiceSelected
} from '../utils/clinicsUtils'
import dayjs from 'dayjs'
import $t from '../i18n'
import { getIsHmoMember, isHmoPaymentFlow } from '../utils/hmoUtils'
import {
  getExcludedSubscriptionIds,
  getExcludedDueInvoicesOfSubscriptionsIds
} from '../utils/paymentUtils'

export const checkServiceAvalibility = action$ =>
  action$.pipe(
    getPayload(Actions.APP_LAUNCHED),
    switchMap(() =>
      of(
        Actions.createAction(Actions.SYSTEM_LANGUAGES_REQUESTED),
        Actions.createAction(Actions.BENEFIT_FEATURE_FLAG_REQUESTED),
        Actions.createAction(Actions.APP_CONFIG_REQUESTED),
        Actions.createAction(Actions.CONFIG_VIDEO_PRIVDER_REQUESTED)
      )
    )
  )

export const reloadVccWhenNeeded = action$ =>
  action$.pipe(
    getPayload(Actions.NOTIFICATIONS_RECEIVED),
    filter(({ notifications }) =>
      notifications.some(
        ({ notification_type }) => notification_type === 'vcc_funds_loaded'
      )
    ),
    take(1),
    switchMap(() =>
      of(Actions.createAction(Actions.USER_PROFILE_EXTENDED_REQUESTED))
    )
  )

export const reloadPatientDocOnNewNotifications = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.NOTIFICATIONS_RECEIVED),
    withLatestFrom(state$),
    filter(([{ notifications }, state]) => {
      const prevNotifications = state?.notifications?.prevNotifications
      // check if there is new notifications by comparing ids
      return prevNotifications?.length
        ? prevNotifications.some(
            prevNotification =>
              !notifications.some(
                notification => notification?.id === prevNotification?.id
              )
          )
        : notifications.length > 0
    }),
    switchMap(() => of(Actions.createAction(Actions.PATIENT_DOCS_REQUESTED)))
  )

export const requestIsCouponValidZuora = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.IS_COUPON_VALID_REQUESTED),
    withLatestFrom(state$),
    filter(([, state]) => state.app.features.billing_enabled),
    switchMap(([{ customerCoupon, selectedPlan }, state]) => {
      return postJSON(`/zuora/coupon-code-validate-apply/`, {
        customerCoupon: customerCoupon,
        selectedRatePlanId: selectedPlan?.id,
        selectedRatePlanChargeId: selectedPlan?.charge.id,
        billingState: state.app.locationState
      }).pipe(
        switchMap(data =>
          of(
            Actions.createAction(Actions.IS_COUPON_VALID_RECEIVED, {
              data,
              customerCoupon,
              selectedPlan
            }),
            // Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
            //   eventName: Actions.IS_COUPON_VALID_RECEIVED + '_WEB',
            // })
            Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
              type: eventTypes.formEvent,
              eventName: event.formEvent.submit,
              data: {
                plan_id: selectedPlan?.id,
                plan_name: selectedPlan?.plan_name,
                coupon: customerCoupon
              },
              element: 'Use Coupon'
            })
          )
        ),
        catchError(e =>
          of(
            Actions.createAction(
              Actions.OPERATION_FAILED,
              translateErrorMsg(e)
            ),
            // Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
            //   eventName: Actions.INVALD_COUPON_RECIEVED + '_WEB',
            // }),
            Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
              type: eventTypes.formEvent,
              eventName: event.formEvent.invalid,
              data: {
                plan_id: selectedPlan?.id,
                plan_name: selectedPlan?.plan_name,
                coupon: customerCoupon
              },
              element: 'Use Coupon',
              error: formatClientSideError({ error: e?.body?.message })
            }),
            Actions.createAction(Actions.PROCEED_TO_CHECKOUT_PRESSED_CANCELLED),
            Actions.createAction(Actions.INVALD_COUPON_RECIEVED, e)
          )
        )
      )
    })
  )

export const requestStripeBillingPortal = action$ =>
  action$.pipe(
    getPayload(Actions.BILLING_PORTAL_REQUESTED),
    switchMap(() => {
      return postJSON(`/payment/portal-session/`).pipe(
        mergeMap(({ billingPortalUri }) => {
          return of(
            Actions.createAction(Actions.BILLING_PORTAL_RECEIVED, {
              billingPortalUri
            }),
            // Actions.createAction(Actions.NAVIGATED_TO, routes.PROFILE_ROUTE.PAYMENT_DETAILS)
            (window.location.href = billingPortalUri)
          )
        }),
        catchAndDispatchCustomError
      )
    })
  )

export const requestPaymentMethodSignature = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.GENERATE_PAYMENT_PAGE_DATA_REQUESTED),
    withLatestFrom(state$),
    switchMap(([{ selectedPlan, customerCoupon }, state]) => {
      return postJSON(`/zuora/pre-payment-page-operations/`, {
        stateCode:
          state.app.locationState ||
          state.payment?.unauthenticatedPaymentDetails?.address?.state,
        customerCoupon: customerCoupon ? customerCoupon : null,
        selectedRatePlanId: selectedPlan?.id,
        selectedRatePlanChargeId: selectedPlan?.charge?.id,
        serviceType: isHmoPaymentFlow(
          state.profile.userDetails,
          state.payment.selectedPlan
        )
          ? 'hmo'
          : 'telehealth',
        excludedSubscriptionIds: getExcludedSubscriptionIds(
          state.payment?.paymentConfigPerSubscriptions
        ),
        excludedDueInvoicesOfSubscriptionsIds:
          getExcludedDueInvoicesOfSubscriptionsIds(
            state.payment?.paymentConfigPerSubscriptions
          )
      }).pipe(
        map(data =>
          Actions.createAction(
            Actions.GENERATE_PAYMENT_PAGE_DATA_RECEIVED,
            data
          )
        ),
        catchError(e => {
          if (e.status === 400) {
            return of(
              Actions.createAction(
                Actions.PROCEED_TO_CHECKOUT_PRESSED_CANCELLED
              ),
              Actions.createAction(Actions.ENABLE_NO_SERVICE_DIALOG)
            )
          } else {
            return of(
              Actions.createAction(
                Actions.PROCEED_TO_CHECKOUT_PRESSED_CANCELLED
              ),
              Actions.createAction(Actions.ENABLE_SOMETHING_WENT_WRONG_DIALOG)
            )
          }
        })
      )
    })
  )

export const requestCheckoutSessionForNoMember = action$ =>
  action$.pipe(
    getPayload(Actions.CHECKOUT_SESSION_NO_MEMBER_REQUESTED),
    switchMap(
      ({ selectedPlan, firstName, lastName, email, state, phoneNumber }) => {
        let lowerEmail = email.toLowerCase()
        return postJSON(`/core/check_no_signup_username/${lowerEmail}/`, {
          state,
          phoneNumber,
          firstName,
          lastName,
          selected_plan_pk: selectedPlan
        }).pipe(
          mergeMap(data => {
            return of(
              Actions.createAction(Actions.CHECKOUT_SESSION_RECEIVED, {
                payload: data
              }),
              Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
                eventName: Actions.CHECKOUT_SESSION_RECEIVED + '_WEB'
              })
            )
          }),
          catchAndCancelActions([
            Actions.createAction(Actions.PROCEED_TO_CHECKOUT_PRESSED_CANCELLED),
            Actions.createAction(Actions.CHECKOUT_SESSION_REQUESTED_FAILURE, {
              selectedPlan
            }),
            Actions.createAction(Actions.LOCATION_VERIFICATION_FILLED)
          ])
        )
      }
    )
  )

export const requestCheckoutSessionForNoMemberPPF = action$ =>
  action$.pipe(
    getPayload(Actions.CHECKOUT_SESSION_NO_MEMBER_REQUESTED_PPF),
    switchMap(
      ({
        selectedPlan,
        firstName,
        lastName,
        email,
        state,
        phoneNumber,
        dob,
        source,
        isGiftingMVP,
        stateCode,
        selectedPlanObj,
        element
      }) => {
        let lowerEmail = email.toLowerCase()
        return postJSON(`/core/check_no_signup_username/${lowerEmail}/`, {
          state,
          phoneNumber,
          firstName,
          lastName,
          dob: dayjs(dob)?.format('YYYY-MM-DD'),
          source,
          isGiftingMVP,
          selectedRatePlanId: selectedPlanObj?.id,
          selectedRatePlanChargeId: selectedPlanObj?.charge?.id,
          stateCode: stateCode
        }).pipe(
          mergeMap(data => {
            // in case the ppf is without payment flow
            if (selectedPlanObj?.is_without_payment) {
              return of(
                Actions.createAction(
                  Actions.CHECKOUT_SESSION_WITHOUT_PAYMENT_REQUESTED,
                  {
                    selectedPlan: selectedPlanObj?.id,
                    firstName,
                    lastName,
                    email: lowerEmail,
                    phoneNumber: `+${phoneNumber.code}${phoneNumber.phone}`,
                    dob,
                    stateCode: stateCode,
                    selectedPlanObj,
                    element: 'ppf-form'
                  }
                )
              )
            }
            // else, start payment flow
            return of(
              Actions.createAction(
                Actions.GENERATE_PAYMENT_PAGE_DATA_RECEIVED,
                data
              ),

              Actions.createAction(Actions.KEEP_PPF_USER_INFO, {
                email,
                state,
                phoneNumber,
                firstName,
                lastName,
                dob,
                source,
                isGiftingMVP,
                stateCode: stateCode,
                selectedPlanObj: selectedPlanObj
              }),
              // Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
              //   eventName: Actions.CHECKOUT_SESSION_RECEIVED + '_WEB',
              // }),
              Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
                type: eventTypes.formEvent,
                eventName: event.formEvent.submit,
                data: {
                  selectedPlan,
                  firstName,
                  lastName,
                  email,
                  state,
                  phoneNumber,
                  dob
                },
                element
              })
            )
          }),
          catchAndCancelActions([
            Actions.createAction(Actions.PROCEED_TO_CHECKOUT_PRESSED_CANCELLED),
            Actions.createAction(Actions.CHECKOUT_SESSION_REQUESTED_FAILURE, {
              selectedPlan
            }),
            Actions.createAction(Actions.LOCATION_VERIFICATION_FILLED)
          ])
        )
      }
    )
  )

export const requestWalnutCheckoutSessionForNoMember = action$ =>
  action$.pipe(
    getPayload(Actions.CHECKOUT_WALNUT_NO_MEMBER_REQUESTED),
    switchMap(
      ({ selectedPlan, firstName, lastName, phoneNumber, plan, email }) => {
        return postJSON(`/walnut/app/`, {
          phoneNumber: phoneNumber,
          firstName: firstName,
          lastName: lastName,
          requestedAmount: plan.price,
          procedureName: plan?.clinic ? plan?.clinic : 'Antidote Services',
          metaData: {
            selected_plan_pk: selectedPlan,
            email: email
          }
        }).pipe(
          mergeMap(data => {
            return of(
              Actions.createAction(Actions.CHECKOUT_WALNUT_RECEIVED, {
                payload: data
              }),
              Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
                eventName: Actions.CHECKOUT_WALNUT_RECEIVED + '_WEB'
              })
            )
          }),
          catchAndCancelActions([
            Actions.createAction(Actions.PROCEED_TO_CHECKOUT_PRESSED_CANCELLED),
            Actions.createAction(Actions.CHECKOUT_SESSION_REQUESTED_FAILURE, {
              selectedPlan
            })
          ])
        )
      }
    )
  )

export const requestZuoraPlans = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.SUBSCRIPTION_PLANS_REQUESTED),
    withLatestFrom(state$),
    filter(([, state]) => state.app.features.billing_enabled),
    switchMap(() =>
      getJSON('/zuora/product/rate-plan/').pipe(
        map(data => {
          const visiblePlans = data.filter(plan => plan.UserVisible__c)
          return Actions.createAction(
            Actions.SUBSCRIPTION_PLANS_ZOURA_RECEIVED,
            {
              plans: visiblePlans
            }
          )
        }),
        catchAndDispatchError
      )
    )
  )

export const requestZuoraCopayPlan = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.COPAY_PLAN_DETAILS_REQUESTED),
    withLatestFrom(state$),
    filter(([, state]) => state.app.features.billing_enabled),
    switchMap(([{ copayPk }]) => {
      return getJSON(`/zuora/product/rate-plan/${copayPk}/`).pipe(
        map(data =>
          Actions.createAction(Actions.COPAY_PLAN_DETAILS_RECEIVED, {
            selectedPlan: data
          })
        ),
        catchAndDispatchError
      )
    })
  )

export const createOrderApi = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.CREATE_ORDER_REQUESTED),
    withLatestFrom(state$),
    filter(
      ([{ isHMOMember }, state]) =>
        state.app.features.billing_enabled && !isHMOMember
    ),
    switchMap(([requestData]) =>
      postJSON('/zuora/order/', requestData).pipe(
        map(response =>
          Actions.createAction(Actions.CREATE_ORDER_RECEIVED, {
            response
          })
        ),
        catchError(() => {
          return of(
            Actions.createAction(Actions.UNABLE_TO_PROCESS_ZOURA_PAYMENT),
            Actions.createAction(Actions.DISABLE_ZOURA_PAGE_LOADER)
          )
        })
      )
    )
  )

export const createOrderHmoApi = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.CREATE_ORDER_REQUESTED),
    withLatestFrom(state$),
    filter(
      ([{ isHMOMember }, state]) =>
        state.app.features.billing_enabled && isHMOMember
    ),
    switchMap(([requestData]) =>
      postJSON('/zuora/hmo-order/', requestData).pipe(
        mergeMap(response => {
          return of(
            Actions.createAction(Actions.CREATE_ORDER_RECEIVED, {
              response,
              flow: 'hmo-order'
            }),
            Actions.createAction(Actions.DELAYED_ACTION_REQUESTED, {
              actionName: Actions.USER_PROFILE_REQUESTED,
              delayTime: 5000
            }) // get user profile in 5 seconds delay, after zuora webhook proccecing done in BE, to get updated hmo_status and make payment notification gone
          )
        }),
        catchError(() => {
          return of(
            Actions.createAction(Actions.UNABLE_TO_PROCESS_ZOURA_PAYMENT),
            Actions.createAction(Actions.DISABLE_ZOURA_PAGE_LOADER)
          )
        })
      )
    )
  )

export const createOrderApiPPF = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.CREATE_ORDER_REQUESTED_PPF),
    withLatestFrom(state$),
    filter(([, state]) => state.app.features.billing_enabled),
    switchMap(([requestData]) =>
      postJSON('/zuora/ppf_order/', requestData).pipe(
        tap(response => {
          setAuthToken(response)
        }),
        map(response =>
          Actions.createAction(Actions.CREATE_ORDER_RECEIVED, {
            response
          })
        ),
        catchError(() => {
          return of(
            Actions.createAction(Actions.UNABLE_TO_PROCESS_ZOURA_PAYMENT),
            Actions.createAction(Actions.DISABLE_ZOURA_PAGE_LOADER)
          )
        })
      )
    )
  )

export const requestPlan = action$ =>
  action$.pipe(
    getPayload(Actions.PAYMENT_PLAN_REQUESTED),
    switchMap(({ id }) => {
      return getJSON(`/zuora/product/rate-plan/${id}/`).pipe(
        map(data =>
          Actions.createAction(Actions.PAYMENT_PLAN_SELECTED, {
            selectedPlan: data
          })
        ),
        catchAndDispatchError
      )
    })
  )

export const requestNonSupportedState = action$ =>
  action$.pipe(
    getPayload(Actions.REQUEST_NON_SUPPORTED_STATE),
    switchMap(userData => {
      return postJSON(`/bnpl-non-supported-state/`, userData).pipe(
        map(() => Actions.createAction(Actions.NON_SUPPORTED_STATE_SENDED)),
        catchAndDispatchError
      )
    })
  )

export const pollAppointments = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.APPOINTMENTS_POLL_REQUESTED),
    withLatestFrom(state$),
    switchMap(([, state]) => {
      let url = '/schedule/all-my-dynamic-appointments'
      const location_state = serializeLocationVerification(state).address_state
      if (location_state != null) {
        url = `/schedule/all-my-dynamic-appointments?state=${location_state}`
      }
      return getJSON(url).pipe(
        switchMap(result => {
          // let origin = 'before_picking_time'
          // let appointment_id = null
          // const appointmentInQueue = filterAppointmentsInQueue(
          //   result.appointments
          // )
          // if (appointmentInQueue.length) {
          //   origin = 'appointment_in_queue'
          //   appointment_id =
          //     appointmentInQueue[appointmentInQueue.length - 1]?.id
          // }
          return of(
            // Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
            //   eventName: 'appointment_eta_received_web',
            //   data: {
            //     eta: result.eta,
            //     origin,
            //     appointment_id,
            //   },
            // }),
            Actions.createAction(Actions.APPOINTMENTS_POLL_RECEIVED, result)
          )
        }),
        catchAndDispatchError
      )
    })
  )

export const clearDesignatedPkOnTimeWindowsRequested = action$ =>
  action$.pipe(
    ofType(Actions.APPOINTMENT_TIMEWINDOWS_REQUESTED),
    filter(action => action.payload?.designated !== true),
    mapTo(Actions.createAction(Actions.CLEARED_APPOINTMENT_DESIGNATED_PK))
  )

export const retriveDesignatedTimeWindows = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.APPOINTMENT_TIMEWINDOWS_REQUESTED),
    withLatestFrom(state$),
    filter(([, state]) => state.app.isServiceInYourCountry),
    filter(([payload]) => payload?.designated),
    switchMap(([, state]) => {
      const { url, context } = buildTimeWindowsUrl(state, { designated: true })
      return getJSON(url).pipe(
        switchMap(result => {
          // if time windows are empty, show error
          if (!result?.length) {
            return of(
              Actions.createAction(Actions.SHOW_MORE_TIME_OPTIONS_ERROR)
            )
          }
          return of(
            Actions.createAction(
              Actions.APPOINTMENT_DESIGNATED_TIMEWINDOWS_RECEIVED,
              {
                timeWindows: result,
                designatedPk: context.designatedPk
              }
            )
          )
        }),
        catchAndDispatchError
      )
    })
  )

export const retriveTimeWindows = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.APPOINTMENT_TIMEWINDOWS_REQUESTED),
    withLatestFrom(state$),
    // filter(([_, state]) => state.app.isServiceInYourCountry),
    filter(([payload]) => !payload?.designated),
    switchMap(([, state]) => {
      const { url } = buildTimeWindowsUrl(state, { designated: false })
      return getJSON(url).pipe(
        switchMap(result => {
          // if time windows are empty, show error
          if (!result?.length) {
            return of(
              Actions.createAction(Actions.SHOW_MORE_TIME_OPTIONS_ERROR)
            )
          }
          return of(
            Actions.createAction(
              Actions.APPOINTMENT_TIMEWINDOWS_RECEIVED,
              result
            )
          )
        }),
        catchAndDispatchError
      )
    })
  )

export const sendDownloadLinkThroughSMSEpic = action$ =>
  action$.pipe(
    getPayload(Actions.DOWNLOAD_LINK_REQUESTED_THROUGH_SMS),
    switchMap(() => {
      return getJSON('/core/send_download_app_link/').pipe(
        map(result =>
          Actions.createAction(
            Actions.DOWNLOAD_LINK_REQUESTED_THROUGH_SMS_SUCCESS,
            result
          )
        ),
        catchError(e => {
          return of(
            Actions.createAction(
              Actions.DOWNLOAD_LINK_REQUESTED_THROUGH_SMS_FAILURE,
              translateErrorMsg(e)
            )
          )
        })
      )
    })
  )

export const updatePatientSex = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.UPDATE_SEX_REQUESTED),
    withLatestFrom(state$),
    switchMap(([sex, state]) => {
      const id = state.profile.userDetails.id
      return putJSON(`/patient-sex/${id}`, sex).pipe(
        map(() => Actions.createAction(Actions.UPDATE_SEX_RECEIVED)),
        catchAndDispatchError
      )
    })
  )

export const showNoServiceInCountry = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.APPOINTMENT_TIMEWINDOWS_REQUESTED),
    withLatestFrom(state$),
    filter(([, state]) => !state.app.isServiceInYourCountry),
    mapTo(
      Actions.createAction(Actions.SHOW_NO_AVALIBILITY, {
        reason: 'NoServiceInRegion'
      })
    )
  )

export const eta = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.APPOINTMENTS_ETA_REQUESTED),
    withLatestFrom(state$),
    switchMap(([, state]) => {
      let url = '/schedule/get-eta/'
      const location_state = serializeLocationVerification(state).address_state
      if (location_state != null) {
        url = url + `?state=${location_state}`
      }
      return getJSON(url).pipe(
        switchMap(({ eta, block_earliest }) =>
          of(
            Actions.createAction(Actions.APPOINTMENTS_ETA_RECEIVED, {
              eta
            }),
            Actions.createAction(Actions.BLOCK_EARLIEST_STATUS_RECEIVED, {
              block_earliest
            })
          )
        ),
        catchAndDispatchError
      )
    })
  )

export const choosePersonalDoctor = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.CHOOSE_PERSONAL_DOCTOR_REQUESTED),
    withLatestFrom(state$),
    switchMap(([, state]) => {
      const doctor = state.account?.optionalPersonalDoctor
      return postJSON(`/device/personal-doctor/`, {
        pk: doctor?.id
      }).pipe(
        mergeMap(() =>
          of(
            Actions.createAction(Actions.CHOOSE_PERSONAL_DOCTOR_RECEIVED, {
              doctor
            })
          )
        ),
        catchAndDispatchError
      )
    })
  )

export const personalDoctorDetails = action$ =>
  action$.pipe(
    getPayload(Actions.PERSONAL_DOCTOR_DETAILS_REQUESTED),
    switchMap(({ doctor }) => {
      return getJSON(`/device/personal-doctor-details/${doctor?.id}`).pipe(
        mergeMap(({ doctor }) =>
          of(
            Actions.createAction(Actions.PERSONAL_DOCTOR_DETAILS_RECEIVED, {
              doctor
            })
          )
        ),
        catchAndDispatchError
      )
    })
  )

export const createDynamicAppointment = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.CREATE_APPOINTMENT_REQUESTED),
    // filter(timeWindow => timeWindow === 'earliest'),
    throttleTime(2000),
    withLatestFrom(state$),
    switchMap(([payload, state]) => {
      const { timeWindow } = payload
      const appointmentLocation = serializeLocationVerification(state)
      const id = state.account?.selectedMember?.id
        ? state.account?.selectedMember?.id
        : state.profile?.userDetails?.id
      const isHMOMember = getIsHmoMember(state.profile.userDetails)
      const selectedService =
        // in case the user isHMO and the selectedService is primary, send primaryHMO as selectedClinic
        state.getCare?.selectedService === clinicTypes.PRIMARY && isHMOMember
          ? clinicTypes.PRIMARY + HMOService
          : state.getCare?.selectedService
      const designatedPk = state.appointments?.designatedPk
      const body = {
        option: timeWindow,
        other_participant_id: state.appointments?.invitedMember?.id,
        clinic: selectedService,
        appointment_location: appointmentLocation,
        intent: state.getCare?.hmoSelectedIntent
      }
      if (designatedPk != null) {
        body.doctor_id = designatedPk
      }
      return putJSON(`/schedule/create-dynamic-appointment/${id}`, body).pipe(
        mergeMap(appointment => {
          const createAppointmentRecievedAction = Actions.createAction(
            Actions.CREATE_APPOINTMENT_RECEIVED,
            appointment
          )
          const appointmentInQueue = filterAppointmentsInQueue(
            state.appointments.appointments
          )
          const createAppointmentRecievedAnalyticsAction = Actions.createAction(
            Actions.ANALYTICS_EVENT_FIRED,
            {
              eventName: Actions.CREATE_APPOINTMENT_RECEIVED + '_WEB',
              data: { appointment }
            }
          )

          const ETAReceivedAnalyticsAction = Actions.createAction(
            Actions.ANALYTICS_EVENT_FIRED,
            {
              eventName: 'appointment_eta_received_web',
              data: {
                eta: state.appointments.eta,
                origin: 'appointment_in_queue',
                appointment_id:
                  appointmentInQueue[appointmentInQueue.length - 1]?.id
              }
            }
          )
          if (state.appointments.selectedTimeOption === 'earliest') {
            return of(
              createAppointmentRecievedAction,
              createAppointmentRecievedAnalyticsAction,
              ETAReceivedAnalyticsAction
            )
          } else {
            return of(
              createAppointmentRecievedAction,
              createAppointmentRecievedAnalyticsAction
            )
          }
        }),
        catchError(e =>
          of(
            Actions.createAction(
              Actions.OPERATION_FAILED,
              translateErrorMsg(e)
            ),
            Actions.createAction(Actions.CREATE_APPOINTMENT_FAILED),
            Actions.createAction(Actions.NAVIGATED_TO, routes.ROOT)
          )
        )
      )
    })
  )

export const talkNow = action$ =>
  action$.pipe(
    getPayload(Actions.TALK_TO_A_DOCTOR_NOW_REQUESTED),
    switchMap(() => {
      return of({}).pipe(
        mapTo(Actions.createAction(Actions.TALK_TO_A_DOCTOR_NOW_RECEIVED, {})),
        catchAndDispatchError
      )
    })
  )

// export const userProfilePicture = action$ =>
//   action$.pipe(
//     getPayload(Actions.USER_PROFILE_PICTURE_REQUESTED),
//     switchMap(() => {
//       return getJSON('/profile-picture/').pipe(
//         map(profile =>
//           Actions.createAction(Actions.USER_PROFILE_PICTURE_RECEIVED, {
//             profile
//           })
//         ),
//         catchAndDispatchError
//       )
//     }),
//     takeUntil(action$.pipe(ofType(Actions.SIGNOUT_REQUESTED)))
//   )

export const lazyloadRestOfProfile = action$ => {
  return action$.pipe(
    getPayload(Actions.USER_PROFILE_EXTENDED_REQUESTED),
    switchMap(() => {
      return getJSON('/device/profile-extended/').pipe(
        map(payload =>
          Actions.createAction(Actions.USER_PROFILE_EXTENDED_RECEIVED, payload)
        ),
        catchAndDispatchError
      )
    })
    // takeUntil(action$.pipe(ofType(Actions.SIGNOUT_REQUESTED)))
  )
}

export const loadUsedData = action$ =>
  action$.pipe(
    getPayload(Actions.USER_PROFILE_EXTENDED_RECEIVED),
    pluck('account_data'),
    map(payload => Actions.createAction(Actions.ACCOUNT_DATA_RECEIVED, payload))
  )
export const loadWalletData = action$ =>
  action$.pipe(
    getPayload(Actions.USER_PROFILE_EXTENDED_RECEIVED),
    pluck('wallet_data'),
    map(payload => Actions.createAction(Actions.WALLET_DATA_RECEIVED, payload))
  )

export const loadVccCard = action$ =>
  action$.pipe(
    getPayload(Actions.USER_PROFILE_EXTENDED_RECEIVED),
    pluck('card'),
    map(payload => Actions.createAction(Actions.VCC_RECEIVED, payload))
  )

export const loadProfilePicture = action$ =>
  action$.pipe(
    getPayload(Actions.USER_PROFILE_EXTENDED_RECEIVED),
    // tap(() => 'here'),
    pluck('profilePicture'),
    map(payload =>
      Actions.createAction(Actions.PROFILE_PICTURE_RECEIVED, payload)
    )
  )

export const loadClinicsData = action$ =>
  action$.pipe(
    getPayload(Actions.USER_PROFILE_EXTENDED_RECEIVED),
    pluck('clinics'),
    map(payload => Actions.createAction(Actions.CLINICS_DATA_RECEIVED, payload))
  )

export const loadLocationData = action$ =>
  action$.pipe(
    getPayload(Actions.USER_PROFILE_EXTENDED_RECEIVED),
    pluck('last_location'),
    map(payload =>
      Actions.createAction(Actions.LAST_LOCATION_RECEIVED, payload)
    )
  )

export const updateEmergencyContact = action$ =>
  action$.pipe(
    getPayload(Actions.UPDATE_EMERGENCY_CONTACT_REQUESTED),
    concatMap(({ patientId, contactName, phoneNumber }) => {
      return postJSON(`/emergency-contact/${patientId}`, {
        contactName,
        phoneNumber
      }).pipe(
        mergeMap(() =>
          of(
            Actions.createAction(Actions.UPDATE_EMERGENCY_CONTACT_RECEIVED, {
              contactName,
              phoneNumber,
              patientId
            }),
            Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
              type: eventTypes.formEvent,
              eventName: event.formEvent.submit,
              data: {
                contactName,
                phoneNumber
              },
              element: 'Emergency Contact'
            })
          )
        ),
        catchAndDispatchError
      )
    })
  )

export const updateMyPharmacy = action$ =>
  action$.pipe(
    getPayload(Actions.UPDATE_MY_PHARMACY_REQUESTED),
    concatMap(({ patientId, pharmacy }) => {
      return postJSON(`/default-pharmacy/${patientId}`, pharmacy).pipe(
        mergeMap(() =>
          of(
            Actions.createAction(Actions.UPDATE_MY_PHARMACY_RECEIVED, {
              pharmacy,
              patientId
            }),
            Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
              type: eventTypes.formEvent,
              eventName: event.formEvent.submit,
              data: pharmacy,
              element: 'Update pharmacy details'
            })
          )
        ),
        catchAndDispatchError
      )
    })
  )

export const searchPharmacy = action$ =>
  action$.pipe(
    getPayload(Actions.SEARCH_PHARMACY_REQUESTED),
    switchMap(({ zipcode }) => {
      return getJSON(
        `/dose-spot/pharmacy_search_patient?zipCode=${zipcode}`
      ).pipe(
        mergeMap(payload =>
          of(
            Actions.createAction(Actions.SEARCH_PHARMACY_RECEIVED, {
              payload
            }),
            Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
              type: eventTypes.formEvent,
              eventName: event.formEvent.submit,
              data: {
                zipcode
              },
              element: 'Add pharmacy details'
            })
          )
        ),
        catchAndDispatchError
      )
    })
  )

export const availability = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.TIMESLOT_AVAILABILITY_CHECK_REQUESTED),
    withLatestFrom(state$),
    switchMap(([, state]) => {
      let idUploaded = state.profile.userDetails.idUploaded
      // if (!state.app.isServiceInYourCountry) {
      //   return of(
      //     Actions.createAction(Actions.TIMESLOT_AVAILABILITY_CHECK_CANCELED, {
      //       reason: 'NoServiceInRegion',
      //     })
      //   )
      // }

      return getJSON('/schedule/available-slots-today/').pipe(
        map(timeSlots =>
          Actions.createAction(Actions.TIMESLOT_AVAILABILITY_CHECK_RECEIVED, {
            idUploaded,
            hasAvailability: timeSlots?.length,
            timeSlots
          })
        ),
        catchAndDispatchError
      )
    })
  )
export const sendInterestedInClinic = action$ =>
  action$.pipe(
    getPayload(Actions.INTERESTED_IN_CLINIC_REQUESTED),
    switchMap(({ clinic }) => {
      const data = { clinic: clinic }

      return postJSON('/device/send-interested-in-clinic/', data).pipe(
        map(() => Actions.createAction(Actions.INTERESTED_IN_CLINIC_RECEIVED)),
        catchAndDispatchError
      )
    })
  )

export const sendPushTokenToServer = action$ =>
  action$.pipe(
    getPayload(Actions.REPORT_TOKEN_REQUESTED),
    switchMap(({ token }) => {
      const data = { push_token: token }

      return putJSON('/device/push_token', data).pipe(
        map(() => Actions.createAction(Actions.REPORT_TOKEN_RECEIVED)),
        catchAndDispatchError
      )
    })
  )

export const updateMentalHealthFollowUpBotInvitation = action$ =>
  action$.pipe(
    getPayload(
      Actions.FOLLOW_UP_MH_BOT_START_REQUESTED,
      Actions.FOLLOW_UP_MH_START_FROM_DEEP_LINK
    ),
    switchMap(({ invitation_id }) => {
      return putJSON(`/device/update_invitation/${invitation_id}`).pipe(
        map(() => Actions.createAction(Actions.OPEN_MH_FOLLOW_UP_BOT_SCREEN)),
        catchAndDispatchError
      )
    })
  )

export const updateHypertensionFollowUpBotInvitation = action$ =>
  action$.pipe(
    getPayload(
      Actions.FOLLOW_UP_HTN_BOT_START_REQUESTED,
      Actions.FOLLOW_UP_HTN_START_FROM_DEEP_LINK
    ),
    switchMap(({ invitation_id }) => {
      return putJSON(`/device/update_invitation/${invitation_id}`).pipe(
        mergeMap(() =>
          of(Actions.createAction(Actions.OPEN_HTN_FOLLOW_UP_BOT_SCREEN))
        ),
        catchAndDispatchError
      )
    })
  )

export const updateDiabetesFollowUpBotInvitation = action$ =>
  action$.pipe(
    getPayload(
      Actions.FOLLOW_UP_DM_BOT_START_REQUESTED,
      Actions.FOLLOW_UP_DM_START_FROM_DEEP_LINK
    ),
    switchMap(({ invitation_id }) => {
      return putJSON(`/device/update_invitation/${invitation_id}`).pipe(
        mergeMap(() =>
          of(Actions.createAction(Actions.OPEN_DM_FOLLOW_UP_BOT_SCREEN))
        ),
        catchAndDispatchError
      )
    })
  )

export const updatePrimaryHmoFollowUpBotInvitation = action$ =>
  action$.pipe(
    getPayload(Actions.FOLLOW_UP_PRIMARY_HMO_BOT_START_REQUESTED),
    switchMap(({ invitation_id, intent }) => {
      return putJSON(`/device/update_invitation/${invitation_id}`).pipe(
        mergeMap(() =>
          of(
            Actions.createAction(
              Actions.OPEN_PRIMARY_HMO_FOLLOW_UP_BOT_SCREEN,
              { intent }
            )
          )
        ),
        catchAndDispatchError
      )
    })
  )

export const triggerNextMentalHealthFollowUpBot = action$ =>
  action$.pipe(
    getPayload(Actions.TRIGGER_NEXT_MH_FOLLOW_UP_BOT_REQUESTED),
    switchMap(() => {
      return postJSON(`/device/trigger_next_mh_followup`).pipe(
        map(() =>
          Actions.createAction(Actions.TRIGGER_NEXT_MH_FOLLOW_UP_BOT_RECEIVED)
        ),
        catchAndDispatchError
      )
    })
  )

export const updateReferralInvitation = action$ =>
  action$.pipe(
    getPayload(Actions.UPDATE_INVITATION_REQUESTED),
    switchMap(({ invitation_id }) => {
      return putJSON(`/device/update_invitation/${invitation_id}`).pipe(
        map(() => Actions.createAction(Actions.UPDATE_INVITATION_RECEIVED)),
        catchAndDispatchError
      )
    })
  )

export const sendVoipTokenToServer = action$ =>
  action$.pipe(
    getPayload(Actions.VOIP_REGISTER_REQUESTED),
    switchMap(({ token }) => {
      const data = { voip_token: token }
      return putJSON('/device/voip_token', data).pipe(
        map(() => Actions.createAction(Actions.VOIP_REGISTER_RECEIVED)),
        catchAndDispatchError
      )
    })
  )

export const sendProfilePictureToServer = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.USER_PROFILE_PICTURE_CHANGE_REQUESTED),
    withLatestFrom(state$),
    map(([action, state]) => ({
      profilePicture: action.profilePicture,
      userId: state.profile.userDetails.id,
      previousProfilePicture: state.profile.previousProfilePicture
    })),
    switchMap(({ profilePicture, userId, previousProfilePicture }) => {
      const data = { profile_picture: profilePicture }

      return putJSON(`/device/profile-picture/${userId}`, data).pipe(
        map(() =>
          Actions.createAction(Actions.USER_PROFILE_PICTURE_CHANGE_RECEIVED)
        ),
        catchError(error => {
          const actions = [
            Actions.createAction(Actions.OPERATION_FAILED, { error })
          ]
          // Only dispatch revert action if there's a previous profile picture
          if (previousProfilePicture !== null) {
            actions.unshift(
              Actions.createAction(Actions.USER_PROFILE_PICTURE_CHANGE_FAILED, {
                error,
                previousProfilePicture
              })
            )
          }
          return of(...actions)
        })
      )
    })
  )

export const uploadFileToServer = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.UPLOAD_FILE_REQUESTED),
    withLatestFrom(state$),
    map(([{ data }, state]) => ({
      data,
      userId: state.profile.userDetails?.id,
      patient_id: state.account?.selectedMember?.id
        ? state.account?.selectedMember?.id
        : state.profile.userDetails.id
    })),
    map(({ data, userId, patient_id }) => ({
      userId,
      patient_id,
      type: data.type,
      content: data.base64,
      name: `${patient_id}-${short.generate()}.${mime.getExtension(
        data.type.replace('jpg', 'jpeg')
      )}`
    })),
    switchMap(({ content, type, name, patient_id }) => {
      return putFormData(`/device/upload-patient-file/`, {
        patient_id,
        content,
        type,
        name
      }).pipe(
        map(() => Actions.createAction(Actions.UPLOAD_FILE_RECEIVED)),
        catchError(error => {
          console.error(error)
          return of(
            Actions.createAction(Actions.OPERATION_FAILED, { error }),
            Actions.createAction(Actions.UPLOAD_FILE_FAILED, { error })
          )
        })
      )
    })
  )

export const isValidState = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.VALIDATE_STATE_REQUESTED),
    filter(({ state }) => state),
    withLatestFrom(state$),
    switchMap(([{ state, onNext }, currentState]) => {
      const memberId =
        currentState.account?.selectedMember?.hmo_member_id ||
        currentState.profile?.userDetails?.hmo_member_id

      // If user is HMO member, use JWT authentication
      // Otherwise, keep it as public endpoint
      const isPublic = !memberId

      const patientId = currentState.account?.selectedMember?.id

      // Build URL with query parameters
      let url = `/is-valid-state/${state}`
      if (patientId) {
        url += `?patientId=${patientId}`
      }

      return getJSON(url, backendUrl, isPublic).pipe(
        switchMap(result => {
          const isValidState = result?.is_valid_state
          if (isValidState && onNext) {
            // for wizard cases, in case state is valid, go to next page
            onNext()
          }
          return of(
            Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
              type: eventTypes.formEvent,
              eventName: event.formEvent.submit,
              data: {
                state
              },
              element: isValidState ? 'Valid state' : 'Invalid state'
            }),
            Actions.createAction(Actions.VALIDATE_STATE_RECEIVED, result)
          )
        }),
        catchAndDispatchError
      )
    })
  )

export const isSubscriptionValidState = action$ =>
  action$.pipe(
    getPayload(Actions.SUBSCRIPTION_VALIDATE_STATE_REQUESTED),
    filter(({ state }) => state),
    switchMap(({ state }) => {
      return getJSON(
        `/is-subscription-valid-state/${state}`,
        backendUrl,
        true
      ).pipe(
        switchMap(result =>
          of(
            Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
              eventName: 'is_valid_state',
              data: result
            }),
            Actions.createAction(
              Actions.VALIDATE_STATE_RECEIVED_EXTERNAL,
              result
            )
          )
        ),
        catchAndDispatchError
      )
    })
  )

export const noServiceInYourState = action$ =>
  action$.pipe(
    getPayload(Actions.VALIDATE_STATE_RECEIVED),
    filter(({ is_valid_state }) => !is_valid_state),
    mapTo(Actions.createAction(Actions.NOT_SERVICE_POPUP_SHOWED))
  )

export const noDMShiftInYourState = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.VALIDATE_STATE_RECEIVED),
    filter(({ is_valid_state }) => is_valid_state),
    withLatestFrom(state$),
    map(([, state]) => state),
    filter(state => isDmServiceSelected(state) && !isDmServiceAvailable(state)),
    mapTo(
      Actions.createAction(Actions.NO_SHIFTS, { clinic: clinicTypes.DIABETES })
    )
  )

export const noHTNShiftInYourState = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.VALIDATE_STATE_RECEIVED),
    filter(({ is_valid_state }) => is_valid_state),
    withLatestFrom(state$),
    map(([, state]) => state),
    filter(
      state => isHtnServiceSelected(state) && !isHtnServiceAvailable(state)
    ),
    mapTo(Actions.createAction(Actions.NO_SHIFTS, { clinic: clinicTypes.HTN }))
  )

export const noMHShiftInYourState = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.VALIDATE_STATE_RECEIVED),
    filter(({ is_valid_state }) => is_valid_state),
    withLatestFrom(state$),
    map(([, state]) => state),
    filter(state => isMhServiceSelected(state) && !isMhServiceAvailable(state)),
    mapTo(
      Actions.createAction(Actions.NO_SHIFTS, { clinic: clinicTypes.MENTAL })
    )
  )

const isPaymentPlansRoute = state =>
  state.router.location.pathname === routes.PAYMENT_PLANS

export const noShiftInYourState = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.VALIDATE_STATE_RECEIVED),
    filter(({ is_valid_state }) => is_valid_state),
    withLatestFrom(state$),
    map(([, state]) => state),
    filter(
      state =>
        !isPaymentPlansRoute(state) &&
        isPrimaryServiceSelected(state) &&
        !isPrimaryServiceAvailable(state)
    ),
    switchMap(state => {
      const clinic = getIsHmoMember(state?.profile?.userDetails)
        ? clinicTypes.PRIMARY + HMOService
        : clinicTypes.PRIMARY
      return of(Actions.createAction(Actions.NO_SHIFTS, { clinic }))
    })
  )

export const locationVerificationFilledHTN = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.VALIDATE_STATE_RECEIVED),
    filter(({ is_valid_state }) => is_valid_state),
    withLatestFrom(state$),
    map(([, state]) => state),
    filter(
      state => isHtnServiceSelected(state) && isHtnServiceAvailable(state)
    ),
    mapTo(Actions.createAction(Actions.LOCATION_VERIFICATION_FILLED))
  )
export const locationVerificationFilledDM = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.VALIDATE_STATE_RECEIVED),
    filter(({ is_valid_state }) => is_valid_state),
    withLatestFrom(state$),
    map(([, state]) => state),
    filter(state => isDmServiceSelected(state) && isDmServiceAvailable(state)),
    mapTo(Actions.createAction(Actions.LOCATION_VERIFICATION_FILLED))
  )

export const locationVerificationFilledMH = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.VALIDATE_STATE_RECEIVED),
    filter(({ is_valid_state }) => is_valid_state),
    withLatestFrom(state$),
    map(([, state]) => state),
    filter(state => isMhServiceSelected(state) && isMhServiceAvailable(state)),
    mapTo(Actions.createAction(Actions.LOCATION_VERIFICATION_FILLED))
  )

export const locationVerificationFilledPrimary = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.VALIDATE_STATE_RECEIVED),
    filter(({ is_valid_state }) => is_valid_state),
    withLatestFrom(state$),
    map(([, state]) => state),
    filter(
      state =>
        isPrimaryServiceSelected(state) && isPrimaryServiceAvailable(state)
    ),
    mapTo(Actions.createAction(Actions.LOCATION_VERIFICATION_FILLED))
  )

export const cancelAppointment = action$ =>
  action$.pipe(
    getPayload(Actions.CANCEL_APPOINTMENT_REQUESTED),
    filter(({ appointmentId }) => appointmentId),
    switchMap(({ appointmentId }) => {
      return postJSON(`/device/cancel-appointment/${appointmentId}`).pipe(
        mapTo(
          Actions.createAction(Actions.CANCEL_APPOINTMENT_RECEIVED, {
            appointmentId
          })
        ),
        catchAndDispatchError
      )
    })
  )

export const rescheduleAppointment = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.RESCHEDULE_APPOINTMENT_REQUESTED),
    withLatestFrom(state$),
    filter(([{ appointmentId }]) => appointmentId),
    switchMap(([{ appointmentId, appointmentMember }, state]) => {
      return postJSON(`/device/cancel-appointment/${appointmentId}`).pipe(
        mergeMap(() =>
          of(
            Actions.createAction(Actions.RESCHEDULE_APPOINTMENT_RECEIVED, {
              appointmentId
            }),
            Actions.createAction(Actions.CHOOSE_FAMILY_MEMBER, {
              member: appointmentMember || state.profile?.userDetails
            })
          )
        ),
        catchAndDispatchError
      )
    })
  )

export const notifications = action$ =>
  action$.pipe(
    getPayload(Actions.NOTIFICATIONS_REQUESTED),
    switchMap(() => {
      return getJSON('/notifications?isWeb=true').pipe(
        switchMap(notifications =>
          of(
            Actions.createAction(Actions.NOTIFICATIONS_RECEIVED, {
              notifications
            })
          )
        ),
        catchAndDispatchError
      )
    })
  )

export const markNotificationsAsSeen = action$ =>
  action$.pipe(
    getPayload(Actions.NOTIFICATIONS_SEEN_REQUESTED),
    switchMap(({ notifications }) => {
      return patchJSON('/notifications', notifications).pipe(
        map(notifications => {
          return Actions.createAction(Actions.NOTIFICATIONS_SEEN_RECEIVED, {
            notifications
          })
        }),
        catchAndDispatchError
      )
    })
  )

export const getTwilioToken = action$ =>
  action$.pipe(
    getPayload(Actions.TWILIO_TOKEN_REQUESTED),
    switchMap(() => {
      return getJSON('/twilio-token/').pipe(
        map(({ token }) => {
          return Actions.createAction(Actions.TWILIO_TOKEN_RECEIVED, {
            token
          })
        }),
        catchAndDispatchError
      )
    })
  )

export const requestAskNicelySlug = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.ASK_NICELY_SLUG_REQUESTED),
    withLatestFrom(state$),
    switchMap(([payload, state]) => {
      let queryParams = uriEncodeSearchParams('', {
        email: state?.profile?.userDetails?.email,
        first_name: state?.profile?.userDetails?.firstName,
        last_name: state?.profile?.userDetails?.lastName,
        template: payload?.template,
        clinician:
          state?.videoCall?.appointment?.participants[1]?.id +
          '_' +
          state?.videoCall?.appointment?.participants[1]?.name,
        visit_id: state?.videoCall?.appointment?.id,
        patient_state: state?.videoCall?.appointment?.appointment_location,
        patient_plan: state?.payment?.my_subscriptions?.[0]?.plan_name
      })
      return getJSON(`/ask-nicely/slug/${queryParams}`).pipe(
        mergeMap(({ slug }) => {
          if (slug) {
            return of(
              Actions.createAction(Actions.ASK_NICELY_SLUG_RECEIVED, {
                slug
              })
            )
          } else {
            Actions.createAction(Actions.NAVIGATED_TO, routes.THANK_YOU_PAGE)
          }
        }),
        catchError(() => {
          return of(
            Actions.createAction(Actions.NAVIGATED_TO, routes.THANK_YOU_PAGE)
          )
        })
      )
    })
  )

export const requestCheckoutSessionWithoutPayment = action$ =>
  action$.pipe(
    getPayload(Actions.CHECKOUT_SESSION_WITHOUT_PAYMENT_REQUESTED),
    switchMap(
      ({
        selectedPlan,
        firstName,
        lastName,
        email,
        phoneNumber,
        dob,
        stateCode,
        selectedPlanObj,
        element
      }) => {
        return postJSON(`/zuora/no-payment-order/`, {
          username: email?.toLowerCase(),
          billingState: stateCode,
          selectedRatePlanId: selectedPlanObj?.id,
          selectedRatePlanChargeId: selectedPlanObj?.charge?.id
        }).pipe(
          mergeMap(data => {
            return of(
              Actions.createAction(
                Actions.CHECKOUT_SESSION_WITHOUT_PAYMENT_RECEIVED,
                data
              ),

              Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
                type: eventTypes.formEvent,
                eventName: event.formEvent.submit,
                data: {
                  selectedPlan,
                  firstName,
                  lastName,
                  email,
                  stateCode,
                  phoneNumber,
                  dob
                },
                element
              })
            )
          }),
          catchAndCancelActions([
            Actions.createAction(
              Actions.CHECKOUT_SESSION_WITHOUT_PAYMENT_FAILURE,
              {
                selectedPlan
              }
            )
          ])
        )
      }
    )
  )

export const updateGenderDetails = action$ =>
  action$.pipe(
    getPayload(Actions.UPDATE_GENDER_DETAILS_REQUESTED),
    switchMap(({ genderIdentity, pronoun, patientId }) => {
      return postJSON(`/update-gender/${patientId}`, {
        genderIdentity,
        pronoun
      }).pipe(
        mergeMap(() =>
          of(
            Actions.createAction(Actions.UPDATE_GENDER_DETAILS_RECEIVED, {
              genderIdentity,
              pronoun
            }),
            Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
              type: eventTypes.formEvent,
              eventName: event.formEvent.submit,
              data: { genderIdentity, pronoun },
              element: 'Update gender details'
            })
          )
        ),
        catchAndDispatchError
      )
    })
  )

export const updateContactInformation = action$ =>
  action$.pipe(
    getPayload(Actions.UPDATE_CONTACT_INFORMATION_REQUESTED),
    switchMap(
      ({
        phone,
        email,
        mailingAddress,
        mailingAddress2,
        mailingCity,
        mailingState,
        mailingZipCode,
        patientId
      }) => {
        return postJSON(`/update-contact-information/${patientId}`, {
          phone,
          email,
          address: mailingAddress,
          address1: mailingAddress2,
          city: mailingCity,
          state: mailingState,
          zipcode: mailingZipCode
        }).pipe(
          mergeMap(() =>
            of(
              Actions.createAction(
                Actions.UPDATE_CONTACT_INFORMATION_RECEIVED,
                {
                  phone,
                  email,
                  mailingAddress,
                  mailingAddress2,
                  mailingCity,
                  mailingState,
                  mailingZipCode
                }
              ),
              Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
                type: eventTypes.formEvent,
                eventName: event.formEvent.submit,
                data: {
                  phone,
                  email,
                  mailingAddress,
                  mailingAddress2,
                  mailingCity,
                  mailingState,
                  mailingZipCode
                },
                element: 'Update contact information'
              })
            )
          ),
          catchError(e => {
            return of(
              Actions.createAction(
                Actions.UPDATE_CONTACT_INFORMATION_FAILED,
                translateErrorMsg(e)
              )
            )
          })
        )
      }
    )
  )

export const checkPhone = action$ =>
  action$.pipe(
    getPayload(Actions.PHONE_NUMBER_VERIFICATION_REQUESTED),
    switchMap(({ phoneNumber }) => {
      return postJSON('/core/check_new_phone/', {
        phone_number: phoneNumber
      }).pipe(
        mergeMap(() =>
          of(
            Actions.createAction(Actions.PHONE_NUMBER_VERIFICATION_RECEIVED),
            Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
              type: eventTypes.formEvent,
              eventName: event.formEvent.submit,
              data: { phone_number: phoneNumber },
              element: 'Enter_the_verification_code'
            })
          )
        ),
        catchError(e => {
          return of(
            Actions.createAction(
              Actions.PHONE_NUMBER_VERIFICATION_FAILED,
              translateErrorMsg(e)
            )
          )
        })
      )
    })
  )

export const verifyCode = action$ =>
  action$.pipe(
    getPayload(Actions.PHONE_NUMBER_CODE_VERIFY_REQUESTED),
    switchMap(({ phoneNumber, value }) => {
      return postJSON('/core/register_verify/', {
        phone_number: phoneNumber,
        code: value
      }).pipe(
        mergeMap(() =>
          of(
            Actions.createAction(Actions.PHONE_NUMBER_CODE_VERIFY_RECEIVED),
            Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
              type: eventTypes.formEvent,
              eventName: event.formEvent.submit,
              data: { phone_number: phoneNumber, code: value },
              element: 'Verify_code'
            })
          )
        ),
        catchAndDispatchError
      )
    })
  )

export const updateSecretQuestions = action$ =>
  action$.pipe(
    getPayload(Actions.SECRET_QUESTION_UPDATE_REQUESTED),
    switchMap(({ questions }) => {
      return postJSON('/secret-questions/', {
        secret_questions: questions
      }).pipe(
        mergeMap(() =>
          of(
            Actions.createAction(Actions.SECRET_QUESTION_UPDATE_RECEIVED),
            Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
              type: eventTypes.formEvent,
              eventName: event.formEvent.submit,
              data: { secret_questions: questions },
              element: 'update-secret-questions'
            })
          )
        ),
        catchError(e => {
          return of(
            Actions.createAction(
              Actions.SECRET_QUESTION_UPDATE_FAILED,
              translateErrorMsg(e)
            )
          )
        })
      )
    })
  )

export const updateLanguage = action$ =>
  action$.pipe(
    getPayload(Actions.UPDATE_LANGUAGE_REQUESTED),
    switchMap(({ language }) => {
      return postJSON(`/update-language/`, {
        language
      }).pipe(
        mergeMap(() =>
          of(
            Actions.createAction(Actions.UPDATE_LANGUAGE_RECEIVED, {
              language
            }),
            Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
              type: eventTypes.formEvent,
              eventName: event.formEvent.submit,
              data: { language },
              element: 'Update language'
            })
          )
        ),
        catchError(e => {
          return of(
            Actions.createAction(
              Actions.UPDATE_LANGUAGE_FAILED,
              translateErrorMsg(e)
            )
          )
        })
      )
    })
  )

export const updateCommunicationPreferences = action$ =>
  action$.pipe(
    getPayload(Actions.UPDATE_COMMUNICATION_PREFERENCES_REQUESTED),
    switchMap(({ preferences, isCommunicationPreferencesConfigureMode }) => {
      return postJSON(`/update-communication-preferences/`, {
        preferences
      }).pipe(
        mergeMap(() =>
          of(
            Actions.createAction(
              Actions.UPDATE_COMMUNICATION_PREFERENCES_RECEIVED,
              {
                preferences
              }
            ),
            // in case we are in configure mode (not on baording flow, from profile), we want to navigate back to the previous screen
            // in case we are on onboarding flow, we want to navigate to the root screen
            isCommunicationPreferencesConfigureMode
              ? Actions.createAction(Actions.NAVIGATED_BACK)
              : Actions.createAction(Actions.NAVIGATED_TO, routes.ROOT),
            Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
              type: eventTypes.formEvent,
              eventName: event.formEvent.submit,
              data: { preferences },
              element: 'Update communication-preferences'
            })
          )
        ),
        catchError(e => {
          return of(
            Actions.createAction(
              Actions.UPDATE_COMMUNICATION_PREFERENCES_FAILED,
              translateErrorMsg(e)
            )
          )
        })
      )
    })
  )

export const getSystemLanguages = action$ =>
  action$.pipe(
    getPayload(Actions.SYSTEM_LANGUAGES_REQUESTED),
    switchMap(() => {
      return getJSON('/get-languages/').pipe(
        map(languages => {
          return Actions.createAction(Actions.SYSTEM_LANGUAGES_RECEIVED, {
            languages
          })
        }),
        catchAndDispatchError
      )
    })
  )

export const getBenefitFeatureFlag = action$ =>
  action$.pipe(
    getPayload(Actions.BENEFIT_FEATURE_FLAG_REQUESTED),
    switchMap(() => {
      return getJSON('/should-show-benefits-feature/').pipe(
        map(({ should_show_benefit_feature }) => {
          return Actions.createAction(Actions.BENEFIT_FEATURE_FLAG_RECEIVED, {
            isEnabled: should_show_benefit_feature
          })
        }),
        catchError(() => {
          return of(
            Actions.createAction(Actions.BENEFIT_FEATURE_FLAG_RECEIVED, {
              isEnabled: true
            })
          )
        })
      )
    })
  )

export const getVideoProvider = action$ =>
  action$.pipe(
    getPayload(Actions.CONFIG_VIDEO_PRIVDER_REQUESTED),
    switchMap(() => {
      return getJSON('/get-video-provider/').pipe(
        map(res => {
          return Actions.createAction(Actions.CONFIG_VIDEO_PRIVDER_RECEIVED, {
            provider: res.provider
          })
        }),
        catchAndDispatchError
      )
    })
  )

export const sendDownloadAppInvite = action$ =>
  action$.pipe(
    getPayload(Actions.INVITE_HMO_MEMBER_TO_DOWNLOAD_APP_REQUESTED),
    switchMap(({ patientId, phoneNumber }) => {
      return getJSON(
        `/core/send_download_app_link/?patientId=${patientId}&phone=${phoneNumber}`
      ).pipe(
        map(() => {
          return Actions.createAction(
            Actions.INVITE_HMO_MEMBER_TO_DOWNLOAD_APP_RECEIVED,
            { patientId }
          )
        }),
        catchError(e => {
          return of(
            Actions.createAction(
              Actions.INVITE_HMO_MEMBER_TO_DOWNLOAD_APP_FAILED,
              translateErrorMsg(e)
            )
          )
        })
      )
    })
  )

export const popMessageDownloadAppInvite = action$ =>
  action$.pipe(
    getPayload(Actions.INVITE_HMO_MEMBER_TO_DOWNLOAD_APP_RECEIVED),
    mapTo(
      Actions.createAction(Actions.MESSAGE_DISPLAYED, {
        content: $t('pages.inviteApp.invitationSent') + '!',
        type: 'success'
      })
    )
  )

export const HMOConsentAggrement = action$ =>
  action$.pipe(
    getPayload(Actions.HMO_CONSENT_AGREEMENT_REQUESTED),
    switchMap(() => {
      return postJSON(`/documents/oh-consent/`).pipe(
        map(payload =>
          Actions.createAction(Actions.HMO_CONSENT_AGREEMENT_RECEIVED, payload)
        ),
        catchAndDispatchError
      )
    })
  )

export const getAppConfig = action$ =>
  action$.pipe(
    getPayload(Actions.APP_CONFIG_REQUESTED),
    switchMap(() => {
      // The supported states list is filtered per token, so this has to carry
      // the token too or a restricted state never reaches the state picker
      return getJSON(appendDirectoryAccessSource(`/get-app-config/`)).pipe(
        map(payload =>
          Actions.createAction(Actions.APP_CONFIG_RECEIVED, payload)
        ),
        catchAndDispatchError
      )
    })
  )

export const fireDelayedAction = action$ =>
  action$.pipe(
    getPayload(Actions.DELAYED_ACTION_REQUESTED),
    switchMap(({ actionName, actionPayload, delayTime }) => {
      return of(Actions.createAction(actionName, actionPayload)).pipe(
        delay(delayTime)
      )
    })
  )

export const handleOperationFailed = action$ =>
  action$.pipe(
    getPayload(Actions.OPERATION_FAILED),
    map(({ error }) => {
      const errorMessage =
        error?.body?.non_field_errors?.join(', ') ||
        error?.body?.detail ||
        error?.body?.message ||
        error?.message ||
        $t('errors.somethingWentWrong')

      return Actions.createAction(Actions.MESSAGE_DISPLAYED, {
        content: errorMessage,
        type: 'error',
        duration: 5
      })
    })
  )
